import { Button } from '@/components/ui/button'
import { DisabledSurfaceOverlay } from '@/components/patterns/disabled-surface-overlay'
import { Card, CardContent } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { ReadOnly } from '@/lib/casl'
import {
  designCollectionsFilters,
  useCollections,
} from '@/lib/services/collections/collections.query'
import { useMerchandisingTagGroups } from '@/lib/services/merchandising-tags/merchandising-tags.query'
import {
  filterSeasonalContentTagGroups,
  flattenMerchandisingTagGroups,
} from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import { useOrganization } from '@/lib/services/organizations/organizations.query'
import { locationFilters, useLocations } from '@/lib/services/locations/locations.query'
import { designProductsFilters, useProducts } from '@/lib/services/products/products.query'
import { useSeasonalDesignRecommendationMutation } from '@/lib/services/seasonal-content/seasonal-content.query'
import { useStoreThemes } from '@/lib/services/stores/stores.query'
import { cn } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { Link, useParams } from '@tanstack/react-router'
import { useMediaQuery } from '@uidotdev/usehooks'
import {
  Calendar,
  Check,
  Megaphone,
  Navigation,
  Palette,
  Pencil,
  Repeat2,
  SlidersHorizontal,
  Sparkles,
  Star,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { SeasonalFeaturedCollectionForm } from './FeaturedContentForm'
import HeaderSectionForm from './HeaderSectionForm'
import HeroSectionForm from './HeroSectionForm'
import ScheduleForm from './ScheduleForm'
import SeasonalSuggestionControls from './SeasonalSuggestionControls'
import {
  StorefrontPreviewFormFrame,
  WebsiteEditorSplitLayout,
  WorkspaceSectionCard,
} from './WebsiteDesignWorkspace'
import { EMPTY_COLLECTION_MEDIA_DRAFTS, applyCollectionMediaDrafts } from './collectionMediaDrafts'
import {
  applyMainBlockSortOrder,
  FEATURED_COLLECTION_BLOCK_TYPE,
  getSelectedSeasonalFeaturedCollectionBlock,
  isSeasonalFeaturedCollectionBlock,
  NO_OCCASION_OPTION_ID,
  resolveAutomaticSeasonalFeaturedCollection,
} from './designForm.utils'
import { composeStorefrontPreviewDesign } from './storefrontPreviewConfig'
import { navigationItemsForCustomMode } from './navigationMenuEditor.utils'
import {
  SEASONAL_RECOMMENDATION_INTENT,
  SEASONAL_RECOMMENDATION_RESULT,
} from './seasonalRecommendation.constants'
import { useSeasonalRecommendationWorkflow } from './useSeasonalRecommendationWorkflow'
import { WEBSITE_EDITOR_TARGET_SECTION_KEY } from './websiteEditorTargetSection'

// Collection visibility may change while navigating elsewhere or in another admin tab.
const designCollectionQueryParams = {
  refetchOnMount: 'always',
  refetchOnWindowFocus: 'always',
}
const deliveryLocationFilters = locationFilters().get()

function SeasonalOverrideState({ isEnabled, disabledLabel, framed = true, children }) {
  return (
    <div
      className={cn(
        'relative mt-4 rounded-xl',
        framed && 'rounded-xl border border-border/70 bg-muted/18 p-4'
      )}
    >
      {!isEnabled ? <DisabledSurfaceOverlay label={disabledLabel} bordered={!framed} /> : null}

      <div className={isEnabled ? '' : 'opacity-55 saturate-0'}>
        <ReadOnly isReadOnly={!isEnabled}>{children}</ReadOnly>
      </div>
    </div>
  )
}

export default function SeasonalDesignWorkspace({
  form,
  defaultDesign = null,
  deleteAction = null,
  selectedSeasonalTag = null,
  seasonalTagFirstSetup = false,
  allowInheritedHeroLayout = false,
  collectionMediaDraftController = null,
  notice = null,
  onRecommendationPendingChange = null,
}) {
  const isWideScreen = useMediaQuery('(min-width: 1280px)')
  const { orgId } = useParams({ strict: false })
  const path = usePathHelper()
  const { data: organization } = useOrganization(orgId)
  const { data: locations = [] } = useLocations({ filters: deliveryLocationFilters })
  const { data: merchandisingTagGroups = [] } = useMerchandisingTagGroups({
    organizationId: orgId,
  })
  const seasonalTagGroups = useMemo(
    () => filterSeasonalContentTagGroups(merchandisingTagGroups),
    [merchandisingTagGroups]
  )
  const seasonalTags = useMemo(
    () => flattenMerchandisingTagGroups(seasonalTagGroups),
    [seasonalTagGroups]
  )
  const { data: themes = [] } = useStoreThemes({ organizationId: orgId })
  const { data: collections = [] } = useCollections({
    organizationId: orgId,
    filters: designCollectionsFilters,
    params: designCollectionQueryParams,
  })
  const collectionMediaDrafts =
    collectionMediaDraftController?.drafts ?? EMPTY_COLLECTION_MEDIA_DRAFTS
  const collectionsWithMediaDrafts = useMemo(
    () => applyCollectionMediaDrafts(collections, collectionMediaDrafts),
    [collectionMediaDrafts, collections]
  )
  const { data: products = [] } = useProducts({
    organizationId: orgId,
    filters: designProductsFilters,
  })
  const recommendationMutation = useSeasonalDesignRecommendationMutation()
  const [previewPage, setPreviewPage] = useState('home')
  const [previewDevice, setPreviewDevice] = useState('desktop')
  const [openSectionId, setOpenSectionId] = useState(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const selectedSeasonalTagId = useStore(form.store, (state) => state.values.seasonal_tag_id)
  const {
    cancelRecommendation,
    hasUndo: hasRecommendationUndo,
    health: recommendationHealth,
    requestRecommendation,
    undoRecommendation,
  } = useSeasonalRecommendationWorkflow({
    form,
    defaultDesign,
    seasonalTags,
    selectedSeasonalTagId,
    recommendationMutation,
    onPendingChange: onRecommendationPendingChange,
  })
  const startsWithoutSeasonalTag =
    selectedSeasonalTagId === NO_OCCASION_OPTION_ID ||
    !Number.isFinite(Number(selectedSeasonalTagId)) ||
    Number(selectedSeasonalTagId) <= 0
  const startsAsManualDesign =
    startsWithoutSeasonalTag &&
    (!seasonalTagFirstSetup ||
      Boolean(form.getFieldValue('seasonal.mark_reviewed')) ||
      Boolean(form.getFieldValue('seasonal.florist_reviewed_at')))
  const [isInitialOccasionSetup, setIsInitialOccasionSetup] = useState(
    () => seasonalTagFirstSetup && startsWithoutSeasonalTag && !startsAsManualDesign
  )
  const [isManualOccasionSetup, setIsManualOccasionSetup] = useState(startsAsManualDesign)
  const designName = useStore(form.store, (state) => state.values.name)
  const sectionOverrides = useStore(form.store, (state) => state.values.section_overrides)
  const seasonal = useStore(form.store, (state) => state.values.seasonal)
  const seasonalFeaturedContent = useStore(
    form.store,
    (state) => state.values.pages?.['/']?.blocks?.main
  )
  const homepageHero = useStore(
    form.store,
    (state) => state.values.pages?.['/']?.blocks?.hero?.hero?.[0]
  )
  const customFeaturedCollectionId =
    getSelectedSeasonalFeaturedCollectionBlock(seasonalFeaturedContent)?.collections?.[0]
      ?.collection_id
  const effectiveFeaturedCollection =
    seasonal?.featured_collection_mode === 'custom'
      ? collectionsWithMediaDrafts.find(
          (collection) => Number(collection.id) === Number(customFeaturedCollectionId)
        )
      : resolveAutomaticSeasonalFeaturedCollection(
          collectionsWithMediaDrafts,
          seasonal?.seasonal_tag_id
        )
  const selectedSeasonalTagOption = seasonalTags.find(
    (option) => Number(option.id) === Number(selectedSeasonalTagId)
  )
  const seasonalTagOptions = [
    { id: NO_OCCASION_OPTION_ID, name: 'No occasion or season' },
    ...seasonalTags,
    ...(selectedSeasonalTag && !selectedSeasonalTagOption
      ? [{ ...selectedSeasonalTag, name: selectedSeasonalTag.label ?? selectedSeasonalTag.name }]
      : []),
  ]
  const selectedSeasonalTagLabel = seasonalTagOptions.find(
    (option) => Number(option.id) === Number(selectedSeasonalTagId)
  )?.name
  const inheritedStatus = {
    icon: Palette,
    label: 'Uses everyday',
    tone: 'neutral',
  }
  const customizedStatus = {
    icon: SlidersHorizontal,
    label: 'Customized',
    tone: 'neutral',
  }
  const automaticStatus = {
    icon: Check,
    label: 'Set automatically',
    tone: 'ready',
  }
  const waitingForContentStatus = {
    icon: Sparkles,
    label: 'Waiting for content',
    tone: 'neutral',
  }
  const heroStatus = !sectionOverrides?.hero
    ? inheritedStatus
    : seasonal?.hero_mode === 'local'
      ? customizedStatus
      : seasonal?.seasonal_design_id
        ? automaticStatus
        : waitingForContentStatus
  const featuredStatus = !sectionOverrides?.featured_content
    ? inheritedStatus
    : seasonal?.featured_collection_mode === 'automatic'
      ? automaticStatus
      : customizedStatus
  const navigationStatus = sectionOverrides?.header_nav_menu ? customizedStatus : automaticStatus
  const canSuggestSeasonalLook =
    selectedSeasonalTagId !== NO_OCCASION_OPTION_ID && Number(selectedSeasonalTagId) > 0
  const setSeasonalSectionOpen = (sectionId, nextOpen) => {
    setOpenSectionId(nextOpen ? sectionId : null)
  }
  const isSeasonalSectionOpen = (sectionId) => openSectionId === sectionId
  const everydayDesignLink = path.builder.index().using({ orgId }).search({
    section: 'brand-layout',
  })

  const handleEverydayDesignClick = () => {
    if (typeof window === 'undefined') return

    window.sessionStorage.setItem(WEBSITE_EDITOR_TARGET_SECTION_KEY, 'brand')
  }

  const handleFeaturedCollectionModeChange = (mode, previousMode) => {
    if (mode !== 'custom' || previousMode === 'custom') {
      return
    }

    const suggestedCollection = resolveAutomaticSeasonalFeaturedCollection(
      collectionsWithMediaDrafts,
      seasonal?.seasonal_tag_id
    )
    const suggestedCollectionId = Number(suggestedCollection?.id)
    const collectionId =
      Number.isFinite(suggestedCollectionId) && suggestedCollectionId > 0
        ? suggestedCollectionId
        : null
    const currentBlocks = Array.isArray(seasonalFeaturedContent) ? seasonalFeaturedContent : []
    const featuredBlockIndex = currentBlocks.findIndex(isSeasonalFeaturedCollectionBlock)

    if (featuredBlockIndex >= 0) {
      form.setFieldValue(
        `pages./.blocks.main.${featuredBlockIndex}.collections.0.collection_id`,
        collectionId
      )
    } else {
      form.setFieldValue(
        'pages./.blocks.main',
        applyMainBlockSortOrder([
          {
            type: FEATURED_COLLECTION_BLOCK_TYPE,
            is_enabled: true,
            collections: [{ collection_id: collectionId }],
            props: seasonal?.featured_style ?? {
              layout: 'carousel',
              size: 'sm',
              imageAlign: 'left',
            },
          },
          ...currentBlocks,
        ])
      )
    }

    form.setFieldValue('seasonal.featured_collection_id', collectionId)
    form.setFieldValue('seasonal.effective_featured_collection_id', collectionId)
  }

  const switchToManualSeasonalSetup = () => {
    if (seasonal?.featured_collection_mode !== 'custom') {
      handleFeaturedCollectionModeChange('custom', seasonal?.featured_collection_mode)
    }

    setIsInitialOccasionSetup(false)
    setIsManualOccasionSetup(true)
    form.setFieldValue('seasonal_tag_id', null)
    form.setFieldValue('seasonal.seasonal_tag_id', null)
    form.setFieldValue('seasonal.seasonal_design_id', null)
    form.setFieldValue('seasonal.seasonal_design', null)
    form.setFieldValue('seasonal.hero_mode', 'local')
    form.setFieldValue('seasonal.featured_collection_mode', 'custom')
    form.setFieldValue('seasonal.mark_reviewed', true)
    form.setFieldValue('schedule.timing_mode', 'custom')
    form.setFieldValue('schedule.seasonal_schedule_profile_id', null)
  }

  const handleNavigationOverrideChange = (useCustomNavigation) => {
    const previousMode = form.getFieldValue('seasonal.navigation_mode')
    if (useCustomNavigation && previousMode !== 'custom') {
      const effectiveDesign = composeStorefrontPreviewDesign(
        form.state.values,
        defaultDesign,
        themes,
        collectionsWithMediaDrafts,
        products
      )
      const effectiveNavMenu = effectiveDesign?.pages?.['/']?.blocks?.header?.navMenu?.[0]

      if (effectiveNavMenu) {
        const customNavMenu = structuredClone(effectiveNavMenu)
        customNavMenu.props = { ...(customNavMenu.props ?? {}), itemsMode: 'custom' }
        customNavMenu.nav_items = navigationItemsForCustomMode(
          customNavMenu.nav_items,
          effectiveNavMenu?.props?.itemsMode ?? 'automatic'
        )
        form.setFieldValue('pages./.blocks.header.navMenu.0', customNavMenu)
      }
    }

    form.setFieldValue('seasonal.navigation_mode', useCustomNavigation ? 'custom' : 'automatic')
  }

  const settingsContent = (
    <div className="space-y-4 pb-10 pt-2 xl:pb-56">
      {notice}
      <Card surface="brand" className="gap-0 py-0">
        <CardContent className="space-y-4 p-4 md:p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <Typography variant="eyebrow" tone="interaction" className="block">
                Seasonal website update
              </Typography>
              {isEditingName ? (
                <div className="flex min-w-0 items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <form.AppField name="name">
                      {(field) => (
                        <field.TextInput
                          label="Design name"
                          autoFocus
                          disabled={field.state.disabled}
                          placeholder="Example: Valentine's Day"
                        />
                      )}
                    </form.AppField>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11"
                    onClick={() => setIsEditingName(false)}
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <div className="flex min-w-0 items-center gap-2">
                  <Typography variant="h3" className="truncate pb-0">
                    {String(designName ?? '').trim() || 'New seasonal update'}
                  </Typography>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-11 shrink-0"
                    aria-label="Edit design name"
                    onClick={() => setIsEditingName(true)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                </div>
              )}
              <Typography variant="supporting" className="block">
                {selectedSeasonalTagLabel
                  ? `Built for ${selectedSeasonalTagLabel}`
                  : isManualOccasionSetup
                    ? 'Build this seasonal update manually. Add an occasion or season later if you want suggestions.'
                    : 'Choose an occasion or season for automatic suggestions, or build the look manually.'}
              </Typography>
            </div>

            <form.AppField name="seasonal_tag_id">
              {(field) => (
                <field.OccasionPicker
                  label={null}
                  options={seasonalTagOptions}
                  mode="single"
                  buttonLabel={selectedSeasonalTagLabel ? 'Change occasion' : 'Choose occasion'}
                  defaultOpen={isInitialOccasionSetup}
                  cancelLabel={isInitialOccasionSetup ? 'Build manually instead' : 'Cancel'}
                  applyLabel={isInitialOccasionSetup ? 'Use selection' : 'Apply selection'}
                  requireSelection={isInitialOccasionSetup}
                  dialogTitle="Start with an occasion"
                  dialogDescription="Choose an occasion or season and we will suggest the photo, wording, featured collection, and yearly schedule. You can change every part afterward."
                  onValueChange={async (value) => {
                    const seasonalTagChanged = Number(value) !== Number(selectedSeasonalTagId)
                    if (!seasonalTagChanged) return

                    if (!(Number(value) > 0)) {
                      switchToManualSeasonalSetup()
                      return
                    }

                    if (isManualOccasionSetup) {
                      form.setFieldValue('seasonal.seasonal_tag_id', value)
                      return
                    }

                    const result = await requestRecommendation(value, {
                      intent: isInitialOccasionSetup
                        ? SEASONAL_RECOMMENDATION_INTENT.INITIAL_SETUP
                        : SEASONAL_RECOMMENDATION_INTENT.SEASONAL_TAG_CHANGE,
                      previousSeasonalTag: {
                        formValue: selectedSeasonalTagId,
                        seasonalId: form.getFieldValue('seasonal.seasonal_tag_id'),
                      },
                    })
                    if (
                      isInitialOccasionSetup &&
                      [
                        SEASONAL_RECOMMENDATION_RESULT.APPLIED,
                        SEASONAL_RECOMMENDATION_RESULT.WAITING,
                      ].includes(result)
                    ) {
                      setIsInitialOccasionSetup(false)
                    }
                  }}
                  onCancel={() => {
                    if (isInitialOccasionSetup) {
                      switchToManualSeasonalSetup()
                    }
                  }}
                />
              )}
            </form.AppField>
          </div>
        </CardContent>
      </Card>

      <WorkspaceSectionCard
        id="seasonal-top-bar"
        icon={Megaphone}
        title="Top bar"
        description="Share a seasonal message, deadline, or quick link above your website header."
        status={sectionOverrides?.header_banner ? customizedStatus : inheritedStatus}
        isOpen={isSeasonalSectionOpen('seasonal-top-bar')}
        onOpenChange={(nextOpen) => setSeasonalSectionOpen('seasonal-top-bar', nextOpen)}
      >
        <form.AppField name="section_overrides.header_banner">
          {(field) => (
            <field.SwitchInput
              label="Use a seasonal top bar"
              description="Your everyday top bar stays in place when this is off."
            />
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.section_overrides?.header_banner}>
          {(isEnabled) => (
            <SeasonalOverrideState isEnabled={!!isEnabled} disabledLabel="Using everyday top bar">
              <HeaderSectionForm showNavigation={false} />
            </SeasonalOverrideState>
          )}
        </form.Subscribe>
      </WorkspaceSectionCard>

      <WorkspaceSectionCard
        id="seasonal-navigation"
        icon={Navigation}
        title="Navigation"
        description="Temporarily replace the main menu links shoppers use while this update is active."
        status={navigationStatus}
        isOpen={isSeasonalSectionOpen('seasonal-navigation')}
        onOpenChange={(nextOpen) => setSeasonalSectionOpen('seasonal-navigation', nextOpen)}
      >
        <form.AppField
          name="section_overrides.header_nav_menu"
          listeners={{
            onChange: ({ value }) => handleNavigationOverrideChange(value),
          }}
        >
          {(field) => (
            <field.SwitchInput
              label="Set the navigation order myself"
              description="When this is off, the featured collection automatically moves near the front of your everyday menu."
            />
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.section_overrides?.header_nav_menu}>
          {(isEnabled) => (
            <SeasonalOverrideState
              isEnabled={!!isEnabled}
              disabledLabel="Featured collection moves automatically"
            >
              <HeaderSectionForm showAnnouncementBar={false} showNavigationModeControls={false} />
            </SeasonalOverrideState>
          )}
        </form.Subscribe>
      </WorkspaceSectionCard>

      <WorkspaceSectionCard
        id="seasonal-hero"
        icon={Sparkles}
        title="Homepage banner"
        description="Choose the main homepage image, wording, style, and text placement."
        status={heroStatus}
        isOpen={isSeasonalSectionOpen('seasonal-hero')}
        onOpenChange={(nextOpen) => setSeasonalSectionOpen('seasonal-hero', nextOpen)}
      >
        <form.AppField name="section_overrides.hero">
          {(field) => (
            <field.SwitchInput
              label="Use a seasonal homepage banner"
              description="Replaces your everyday banner while this update is active."
            />
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.section_overrides?.hero}>
          {(isEnabled) => (
            <SeasonalOverrideState
              isEnabled={!!isEnabled}
              disabledLabel="Using everyday homepage banner"
              framed={false}
            >
              <div className="space-y-5">
                {canSuggestSeasonalLook ? (
                  <SeasonalSuggestionControls
                    hero={homepageHero}
                    seasonal={seasonal}
                    hasUndo={hasRecommendationUndo}
                    health={recommendationHealth}
                    isPending={recommendationMutation.isPending}
                    onShuffle={() => requestRecommendation()}
                    onKeepChange={(keep) =>
                      form.setFieldValue('seasonal.keep_for_future_years', keep)
                    }
                    onUndo={undoRecommendation}
                  />
                ) : null}
                <HeroSectionForm
                  allowInheritedLayout={allowInheritedHeroLayout}
                  automaticButtonDestination
                  trackSeasonalCustomization
                />
              </div>
            </SeasonalOverrideState>
          )}
        </form.Subscribe>
      </WorkspaceSectionCard>

      <WorkspaceSectionCard
        id="seasonal-featured"
        icon={Star}
        title="Seasonal featured collection"
        description="Feature one timely collection first while keeping everyday sections like Sympathy, Birthday, reviews, and Contact Us in place."
        status={featuredStatus}
        isOpen={isSeasonalSectionOpen('seasonal-featured')}
        onOpenChange={(nextOpen) => setSeasonalSectionOpen('seasonal-featured', nextOpen)}
      >
        <form.AppField name="section_overrides.featured_content">
          {(field) => (
            <field.SwitchInput
              label="Use seasonal featured collection"
              description="Your everyday featured content stays below it, so you do not have to rebuild the homepage for a holiday."
            />
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.section_overrides?.featured_content}>
          {(isEnabled) => (
            <SeasonalOverrideState
              isEnabled={!!isEnabled}
              disabledLabel="Using everyday featured content"
            >
              <div className="space-y-4">
                {canSuggestSeasonalLook ? (
                  <>
                    <form.AppField name="seasonal.featured_collection_mode">
                      {(field) => (
                        <field.RadioButtonGroup
                          label="Collection selection"
                          className="max-w-xl"
                          classNames={{ container: 'w-full', button: 'min-h-11 min-w-0 flex-1' }}
                          onValueChange={handleFeaturedCollectionModeChange}
                          options={[
                            { id: 'automatic', name: 'Automatically selected' },
                            { id: 'custom', name: 'Choose a collection myself' },
                          ]}
                        />
                      )}
                    </form.AppField>
                    <form.Subscribe
                      selector={(state) => state.values.seasonal?.featured_collection_mode}
                    >
                      {(mode) =>
                        mode === 'custom' ? (
                          <SeasonalFeaturedCollectionForm
                            pageSlug="/"
                            isEnabled={!!isEnabled}
                            collectionsOverride={collectionsWithMediaDrafts}
                            collectionMediaDraftController={collectionMediaDraftController}
                          />
                        ) : (
                          <div className="rounded-xl border bg-muted/20 p-4 text-sm text-muted-foreground">
                            {effectiveFeaturedCollection ? (
                              <>
                                Currently featuring{' '}
                                <span className="font-medium text-foreground">
                                  {effectiveFeaturedCollection.name}
                                </span>
                                . If it becomes unavailable, another valid match takes its place
                                automatically.
                              </>
                            ) : (
                              <>
                                Waiting for a visible collection tagged for this occasion or season
                                with visible products. This update will become ready automatically
                                when one is available.
                              </>
                            )}
                          </div>
                        )
                      }
                    </form.Subscribe>
                  </>
                ) : (
                  <SeasonalFeaturedCollectionForm
                    pageSlug="/"
                    isEnabled={!!isEnabled}
                    collectionsOverride={collectionsWithMediaDrafts}
                    collectionMediaDraftController={collectionMediaDraftController}
                  />
                )}
              </div>
            </SeasonalOverrideState>
          )}
        </form.Subscribe>
      </WorkspaceSectionCard>

      <form.Subscribe
        selector={(state) => {
          const nextSchedule = state.values.schedule

          if (!nextSchedule?.is_enabled) return 'off'
          if (nextSchedule?.timing_mode === 'automatic') return 'automatic'
          if (nextSchedule?.is_yearly || nextSchedule?.start_type === 'holiday_offset') {
            return 'yearly'
          }

          return 'customized'
        }}
      >
        {(scheduleState) => (
          <WorkspaceSectionCard
            id="seasonal-schedule"
            icon={Calendar}
            title="Schedule"
            description="Choose when this update starts and when your storefront returns to normal."
            status={
              scheduleState === 'off'
                ? { icon: Calendar, label: 'Off', tone: 'neutral' }
                : scheduleState === 'automatic'
                  ? automaticStatus
                  : scheduleState === 'yearly'
                    ? { icon: Repeat2, label: 'Set yearly', tone: 'ready' }
                    : customizedStatus
            }
            isOpen={isSeasonalSectionOpen('seasonal-schedule')}
            onOpenChange={(nextOpen) => setSeasonalSectionOpen('seasonal-schedule', nextOpen)}
          >
            <div className="space-y-5">
              <form.AppField name="schedule.is_enabled">
                {(field) => (
                  <field.SwitchInput
                    label="Use this seasonal update"
                    description="Turn this off to keep your everyday website during these dates."
                  />
                )}
              </form.AppField>
              <form.Subscribe selector={(state) => state.values.schedule?.is_enabled}>
                {(isEnabled) =>
                  isEnabled ? (
                    canSuggestSeasonalLook ? (
                      <>
                        <form.AppField name="schedule.timing_mode">
                          {(field) => (
                            <div className="space-y-2">
                              <field.RadioButtonGroup
                                className="max-w-xl"
                                classNames={{
                                  container: 'w-full',
                                  button: 'min-h-11 min-w-0 flex-1',
                                }}
                                label="Timing"
                                options={[
                                  { id: 'automatic', name: 'Use default timing' },
                                  { id: 'custom', name: 'Choose my own timing' },
                                ]}
                                onValueChange={(timingMode, previousTimingMode) => {
                                  if (timingMode === previousTimingMode) return

                                  if (timingMode === 'custom') {
                                    cancelRecommendation()
                                    form.setFieldValue(
                                      'schedule.seasonal_schedule_profile_id',
                                      null
                                    )
                                    return
                                  }

                                  void requestRecommendation(selectedSeasonalTagId, {
                                    intent: SEASONAL_RECOMMENDATION_INTENT.USE_DEFAULT_TIMING,
                                    previousSchedule: {
                                      timingMode: previousTimingMode,
                                      profileId: form.getFieldValue(
                                        'schedule.seasonal_schedule_profile_id'
                                      ),
                                    },
                                  })
                                }}
                              />
                              <Typography variant="supporting" className="block">
                                Default timing follows updates published by the seasonal content
                                team.
                              </Typography>
                            </div>
                          )}
                        </form.AppField>
                        <form.Subscribe selector={(state) => state.values.schedule?.timing_mode}>
                          {(timingMode) =>
                            timingMode === 'custom' ? (
                              <ScheduleForm hideEnabledToggle />
                            ) : (
                              <div className="rounded-xl border bg-muted/20 p-4 text-sm text-muted-foreground">
                                Using the published default schedule for this occasion or season.
                                Choose your own timing to override it only for this update.
                              </div>
                            )
                          }
                        </form.Subscribe>
                      </>
                    ) : (
                      <ScheduleForm hideEnabledToggle />
                    )
                  ) : null
                }
              </form.Subscribe>
            </div>
          </WorkspaceSectionCard>
        )}
      </form.Subscribe>

      <WorkspaceSectionCard
        id="seasonal-brand"
        icon={Palette}
        title="Brand and theme"
        description="Keep your everyday colors, fonts, logo, and overall storefront style."
        status={inheritedStatus}
        isOpen={isSeasonalSectionOpen('seasonal-brand')}
        onOpenChange={(nextOpen) => setSeasonalSectionOpen('seasonal-brand', nextOpen)}
      >
        <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
          <div className="min-w-0">
            <Typography variant="cardTitle">Using your everyday brand and theme</Typography>
            <Typography variant="supporting" className="mt-1 block">
              Seasonal updates intentionally keep the logo, colors, and fonts shoppers already
              recognize. Change those from the everyday website builder when you want the update to
              apply year-round.
            </Typography>
          </div>
          <Button asChild className="mt-4 min-h-11">
            <Link {...everydayDesignLink.link} onClick={handleEverydayDesignClick}>
              <Pencil className="size-4" />
              Edit everyday design
            </Link>
          </Button>
        </div>
      </WorkspaceSectionCard>

      {deleteAction ? (
        <div className="rounded-md border border-status-destructive-border bg-status-destructive-background px-4 py-4">
          <Typography variant="cardTitle">Remove this seasonal website update</Typography>
          <Typography variant="supporting" className="mt-1 block">
            Delete this update if you no longer want to use it for holidays or promotions.
          </Typography>
          {deleteAction}
        </div>
      ) : null}
    </div>
  )

  const settingsPanel = isWideScreen ? (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
      <ScrollArea data-website-editor-scroll-panel className="h-full min-h-0 flex-1">
        {settingsContent}
      </ScrollArea>
    </div>
  ) : (
    <div className="min-w-0">{settingsContent}</div>
  )

  const previewPanel = (
    <div className="h-full min-h-0 overflow-hidden">
      <StorefrontPreviewFormFrame
        form={form}
        locations={locations}
        defaultDesign={defaultDesign}
        organization={organization}
        themes={themes}
        collections={collectionsWithMediaDrafts}
        products={products}
        previewPage={previewPage}
        onPreviewPageChange={setPreviewPage}
        previewDevice={previewDevice}
        onPreviewDeviceChange={setPreviewDevice}
        className="h-full min-h-0"
        viewportClassName="h-full min-h-0"
      />
    </div>
  )

  return (
    <WebsiteEditorSplitLayout
      settingsPanel={settingsPanel}
      previewPanel={previewPanel}
      autoSaveId="website-editor-seasonal-design-panels"
      defaultSettingsSize={36}
      mobileLayout="natural"
    />
  )
}
